/**
 * Garde-fou pour éviter la pollution du cache entre utilisateurs
 * Purge les clés sensibles si l'utilisateur change
 */

import { cacheGet, cacheSet } from './cache';

const ACTIVE_USER_KEY = '__active_user_id';

/**
 * Liste des clés de cache à purger lors d'un changement d'utilisateur
 * Ces clés contiennent des données spécifiques à un utilisateur
 */
const USER_SPECIFIC_KEYS = [
  // Agenda / Opérateurs
  'ops/overrides',
  'ops/overrides:meta',
  'ops/weeklyHours',
  'ops/list',
  'ops/taskPrefs',
  'ops/series7w',
  'ops/series7m',
  'ops/kpiByOp',
  'ops/steriWindow',
  'ops/updatedAt',
  'operators/list',
  'operators/index',
  'operators/weekly-hours/by-id',
  'operators/agg/day',
  'operators/agg/week',
  'operators/agg/month',
  'operators/incidents/day',
  'operators/incidents/week',
  'operators/incidents/month',
  
  // Activity / Attendance
  'activity/attendance',
  'activity/operators',
  'activity/coverage',
  'activity/lastFetch',
  
  // School schedules
  'school/schedules',
  
  // Agenda SSE
  'agenda/logs/cursor',
  'ops/overrides:meta',
  
  // Tasks
  'tasks/list',
  'tasks/stats',
  'tasks/weekly',
  'tasks/operators/day',
  'tasks/operators/week',
  'tasks/operators/month',
  'tasks/cursor',
  'tasks/lastLoad',
  
  // Sterilization
  'steri/stats',
  'steri/weekly',
  'steri/categories',
  'steri/operators',
  'steri/instruments',
  'steri/snapshot.kpi',
  'steri/snapshot.weekSeries'
];

/**
 * Vérifie et purge le cache si l'utilisateur a changé
 * @param {string|number} userId - ID de l'utilisateur actuel
 */
export function ensureCacheForUser(userId) {
  if (!userId) {
    console.warn('⚠️ [cacheUserGuard] userId manquant, purge ignorée');
    return;
  }
  
  const normalizedUserId = String(userId);
  const previousUserId = cacheGet(ACTIVE_USER_KEY, null);
  
  if (previousUserId && previousUserId !== normalizedUserId) {
    console.log(`🧹 [cacheUserGuard] Changement d'utilisateur détecté (${previousUserId} → ${normalizedUserId}), purge du cache...`);
    
    // Purger toutes les clés spécifiques à l'utilisateur
    let purgedCount = 0;
    for (const key of USER_SPECIFIC_KEYS) {
      // Le cache utilise le préfixe 'cc:v2:' par défaut
      const fullKey = `cc:v2:${key}`;
      if (localStorage.getItem(fullKey) !== null) {
        localStorage.removeItem(fullKey);
        purgedCount++;
      }
    }
    
    console.log(`✅ [cacheUserGuard] ${purgedCount} clés purgées`);
  } else if (!previousUserId) {
    console.log(`🆕 [cacheUserGuard] Premier utilisateur détecté: ${normalizedUserId}`);
  } else {
    console.log(`✅ [cacheUserGuard] Même utilisateur (${normalizedUserId}), pas de purge nécessaire`);
  }
  
  // Mémoriser l'utilisateur actuel
  cacheSet(ACTIVE_USER_KEY, normalizedUserId);
}

/**
 * Force la purge du cache (utile au logout)
 */
export function forceCachePurge() {
  console.log('🧹 [cacheUserGuard] Purge forcée du cache...');
  
  let purgedCount = 0;
  for (const key of USER_SPECIFIC_KEYS) {
    const fullKey = `cc:v2:${key}`;
    if (localStorage.getItem(fullKey) !== null) {
      localStorage.removeItem(fullKey);
      purgedCount++;
    }
  }
  
  // Supprimer aussi l'utilisateur actif
  localStorage.removeItem(`cc:v2:${ACTIVE_USER_KEY}`);
  
  console.log(`✅ [cacheUserGuard] ${purgedCount} clés purgées (purge forcée)`);
}

